import { useCallback, useContext, useEffect, useRef } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import i18n from "@/lib/i18n/i18n"
import { CHAT_LINK_PARAM, getLinkedChatJid } from "@/lib/appEntryLink"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import { selectChats } from "@/reducers/chatsSliceSelectors"
import { selectAccountChats } from "@/reducers/accountChatsSlice"
import { peekChat } from "@/reducers/chatsSliceThunks"
import { XmppContext } from "@/stream/xmppClient"

export const CHAT_LINK_RESOLUTION_TIMEOUT_MS = 15_000

export function useChatDeepLink(onOpen: (jid: string) => void) {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { client } = useContext(XmppContext)
  const chats = useAppSelector(selectChats)
  const accountChats = useAppSelector(selectAccountChats)
  const preSync = useAppSelector(
    (state) => state.roomSyncStatus.preRoomSyncInProgress,
  )
  const syncStarted = useAppSelector(
    (state) => state.roomSyncStatus.started !== undefined,
  )
  const hasTarget = searchParams.has(CHAT_LINK_PARAM)
  const target = getLinkedChatJid(searchParams.toString())
  const handled = useRef<string | null>(null)

  const clearTarget = useCallback(() => {
    // Mark it consumed synchronously, before any delayed room data can arrive.
    handled.current = searchParams.toString()
    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete(CHAT_LINK_PARAM)
    navigate(
      { search: nextParams.toString(), hash: window.location.hash },
      { replace: true },
    )
  }, [navigate, searchParams])

  const cancel = useCallback(() => {
    if (hasTarget) clearTarget()
  }, [clearTarget, hasTarget])

  useEffect(() => {
    if (!hasTarget) {
      handled.current = null
      return
    }
    if (handled.current === searchParams.toString()) return
    if (target && (preSync || !syncStarted)) return

    const chat = chats.find((chat) => chat.room && chat.jid === target)
    const accountChat = target ? accountChats[target] : undefined

    handled.current = searchParams.toString()
    if (target && (chat || accountChat)) {
      if (!chat && accountChat) {
        // A shared link opens a room without adding a permanent bookmark.
        dispatch(peekChat(client, accountChat))
      }
      onOpen(target)
    } else {
      toast.error(
        target
          ? i18n.t("app.deepLink.notFound")
          : i18n.t("app.deepLink.invalid"),
      )
    }

    clearTarget()
  }, [
    accountChats,
    chats,
    client,
    dispatch,
    hasTarget,
    onOpen,
    preSync,
    searchParams,
    clearTarget,
    syncStarted,
    target,
  ])

  useEffect(() => {
    const key = searchParams.toString()
    if (!hasTarget || handled.current === key) return

    const fail = (message: string) => {
      if (handled.current === key) return
      toast.error(message)
      clearTarget()
    }
    const onOffline = () => fail(i18n.t("app.deepLink.offline"))
    if (!navigator.onLine) {
      onOffline()
      return
    }

    const timeout = window.setTimeout(
      () => fail(i18n.t("app.deepLink.failed")),
      CHAT_LINK_RESOLUTION_TIMEOUT_MS,
    )
    window.addEventListener("offline", onOffline)
    return () => {
      window.clearTimeout(timeout)
      window.removeEventListener("offline", onOffline)
    }
  }, [clearTarget, hasTarget, searchParams])

  return { isPending: hasTarget, cancel }
}
